import React, { useCallback, useRef, useState } from 'react';
import clsx from 'clsx';
import { PlateElement, useEditorRef, useEditorSelector, useReadOnly, usePluginOptions } from 'platejs/react';
import {
    calculateSelectionRange,
    deleteTableColumnWithSpanFix,
    deleteTableRowWithSpanFix,
    insertTableColumnSized,
    isCellSplitEligible,
} from './tableElementsUtils';
import {
    TablePlugin,
    TableProvider,
    useTableElement,
    useTableCellElement,
    useTableCellElementResizable,
    useTableColSizes,
    useTableValue,
} from '@platejs/table/react';
import { ResizeHandle } from '@platejs/resizable';
import { isSelectingCell, splitTableCell } from '@platejs/table';
import styles from './tableElements.module.scss';
import { CellContextMenu, type CellContextMenuEntry } from '../CellContextMenu/CellContextMenu';
import { TableHoverProvider, useTableHover } from './tableHoverContext';
import { TableHandlesOverlay } from './TableHandlesOverlay';
import {
    CELL_SELECTION_OPTION,
    CELL_TYPES,
    getSelectedCell,
    selectCell,
    selectCellRange,
    clearCellSelection,
    watchCellDrag,
} from './cellSelection';
import { countRender } from '../../debug/platePerf';
import { isPositiveWidth, MAX_TABLE_COL_SIZES_TOTAL_PX, MIN_TABLE_COL_WIDTH_PX } from '../../serializers/tableColSizes';

// Plate v53's table plugins ship no built-in `render: { as }` — they expect
// custom React components. We attach these via `.withComponent()` in
// usePlateEditorLogic so the table renders as real <table>/<tr>/<td>/<th>.
//
// CRITICAL: every table is wrapped in <TableProvider>. The hooks below
// (useTableElement, useTableCellElement, useTableCellElementResizable,
// useTableColSizes) all read from a jotai
// store scoped per-table by that Provider. Without it Plate falls back to a
// shared global store and (a) logs "jotai store 'table' outside of a matching
// provider" on every render, (b) re-renders every table on any store change,
// which clobbers DOM-level drag previews and breaks column resize.

export const TableElement = React.memo(function TableElement(props: any) {
    return (
        <TableProvider>
            <TableHoverProvider>
                <TableElementInner {...props} />
            </TableHoverProvider>
        </TableProvider>
    );
});

function TableElementInner(props: any) {
    const readOnly = useReadOnly();
    const selecting = useEditorSelector((ed: any) => (isSelectingCell as any)(ed), []);
    // Library hook: collapses stale multi-cell selection when the user clicks
    // the table border (spread via tableProps onto the <table>). Provides
    // `marginLeft` for nested-list offset (we read but don't apply it yet).
    const { props: tableProps } = (useTableElement as any)();
    // Effective column sizes, including any drag-time override the resize
    // handle is currently writing to the table store. Rendering <col> widths
    // from this means the drag preview updates live without us touching the
    // DOM ourselves.
    const colSizes: number[] | undefined = (useTableColSizes as any)();
    const { hideDeferred } = useTableHover();
    const tableRef = useRef<HTMLTableElement | null>(null);

    const committedColSizes: unknown = props.element?.colSizes;
    const persistedColSizes: number[] | null =
        Array.isArray(committedColSizes) && committedColSizes.length > 0 && committedColSizes.every(isPositiveWidth)
            ? (committedColSizes as number[])
            : null;
    const persistedTotal = persistedColSizes ? persistedColSizes.reduce((a, w) => a + w, 0) : 0;
    const fitsWithoutCrushing =
        !!persistedColSizes &&
        Math.round(Math.min(...persistedColSizes) * (MAX_TABLE_COL_SIZES_TOTAL_PX / persistedTotal)) >= MIN_TABLE_COL_WIDTH_PX;

    const colPercentTotal = fitsWithoutCrushing && colSizes ? colSizes.reduce((a, w) => a + (isPositiveWidth(w) ? w : 0), 0) : 0;

    // The <table> is wrapped in a plain horizontal-scroll <div> (.tableScroll)
    // so a table wider than the editor content scrolls instead of pushing the
    // page layout out (RECUR-26175 — wide spreadsheet paste). The wrapper is a
    // PLAIN div: no contentEditable / data-slate-* attrs, so Slate treats it as
    // transparent and caret/selection are unaffected. It carries NO padding and
    // only `max-width: 100%`, so unlike the earlier padded-wrapper approach it
    // reserves no space and never pushes a nested table past its column edge.
    //
    // Handles stay OUTSIDE the scroll wrapper: TableHandlesOverlay portals to
    // document.body and positions itself from the live <table> rect, so it needs
    // no positioned ancestor and is unaffected by the wrapper. The in-cell
    // column-resize handles live inside the <table> and resize via a scalar
    // pointer delta (not container-relative coords), so the scroll box is
    // transparent to them too.
    return (
        <>
            <div className={styles.tableScroll}>
                <PlateElement
                    as="table"
                    {...props}
                    {...tableProps}
                    ref={tableRef as any}
                    className={clsx(props.className, selecting && styles.tableSelecting)}
                    style={{ ...(props.style ?? {}), borderCollapse: 'collapse' as const }}
                    onMouseLeave={readOnly ? undefined : hideDeferred}
                >
                    {colSizes && colSizes.length > 0 ? (
                        <colgroup contentEditable={false}>
                            {colSizes.map((w, i) => {
                                if (colPercentTotal > 0) {
                                    return <col key={i} style={{ width: `${((w / colPercentTotal) * 100).toFixed(4)}%` }} />;
                                }
                                return isPositiveWidth(w) ? <col key={i} style={{ width: `${w}px` }} /> : <col key={i} />;
                            })}
                        </colgroup>
                    ) : null}
                    {/* <tbody> wraps the rows so React's validateDOMNesting doesn't warn
                        (`<tr> cannot appear as a child of <table>`). Transparent to slate. */}
                    <tbody>{props.children}</tbody>
                </PlateElement>
            </div>
            {!readOnly && <TableHandlesOverlay tableElement={props.element} tableRef={tableRef} />}
        </>
    );
}

export const TableRowElement = React.memo(function TableRowElement(props: any) {
    const editor = useEditorRef();
    // Back-compat read: the library transform `setTableRowSize` writes `size`,
    // older docs persisted `height`. Render either so legacy rows still load
    // at their saved height after the resize path switches to the library API.
    const persistedRowSize: number | undefined = props.element?.size ?? props.element?.height;

    // PERF: row resize was visibly laggier than column resize because the
    // library's default plumbing applies the row-size override as `minHeight`
    // on every cell via useTableCellElement() — N cells × ~60 ticks/sec of
    // drag = N reflows per pointer-move. Column resize doesn't have this
    // problem because its override flows into a single <colgroup> at the
    // table level (1 reflow per tick, browser reflows the whole column for
    // free under `table-layout: fixed`).
    //
    // Fix: read the row-size override map from the table store ONCE and
    // apply `height` at the <tr> level. Cells inside stretch to fill via
    // standard table-cell vertical layout (vertical-align: top from our
    // editor CSS). One reflow per row per tick — drag is now smooth.
    //
    // Trade-off: useTableValue('rowSizeOverrides') subscribes to the whole
    // map, so every TableRowElement re-renders on any row drag. That's N
    // TR renders vs the previous N*M cell renders — still a big win.
    const overrideMap: any = (useTableValue as any)('rowSizeOverrides');
    // PERF: editor.api.findPath is O(document size), and this row re-renders on
    // every row-resize tick (useTableValue subscribes to the whole override
    // map). Only resolve the row index — and pay findPath — when an override is
    // actually active (mid-drag). In the common case (load, typing, scrolling)
    // there are no overrides, so we use the persisted height directly and skip
    // findPath entirely. Behaviour is identical: with no override the effective
    // height has always been persistedRowSize.
    const hasActiveOverrides = overrideMap && typeof overrideMap.get === 'function' && (overrideMap.size ?? 0) > 0;
    let overrideHeight: number | undefined;
    if (hasActiveOverrides) {
        try {
            const path = (editor as any)?.api?.findPath?.(props.element);
            const rowIndex = Array.isArray(path) && path.length > 0 ? path[path.length - 1] : undefined;
            if (rowIndex != null) {
                const h = overrideMap.get(rowIndex);
                if (typeof h === 'number') overrideHeight = h;
            }
        } catch {
            /* defensive */
        }
    }
    const effectiveHeight = typeof overrideHeight === 'number' ? overrideHeight : persistedRowSize;

    return (
        <PlateElement
            as="tr"
            {...props}
            style={{ ...(props.style ?? {}), ...(effectiveHeight ? { height: `${effectiveHeight}px` } : {}) }}
        />
    );
});

// Shared empty entries returned while the context menu is closed, so a closed
// menu never allocates a fresh array on every Cell render.
const EMPTY_CELL_MENU_ENTRIES: CellContextMenuEntry[] = [];

function useCellMenu(cellElement: any, isInSelection: boolean) {
    const editor = useEditorRef();
    const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null);
    // Split-eligibility is resolved imperatively when the menu opens, from the
    // cell node's own colSpan/rowSpan. The library's useTableMergeState is
    // gone: with TablePlugin's disableMerge on, it early-returns, and without
    // disableMerge it registered THREE editor-wide useEditorSelector
    // subscriptions per cell — one returning a fresh array every editor
    // change, re-rendering every mounted cell on every keystroke/caret move.
    const [canSplit, setCanSplit] = useState(false);

    const open = (e: React.MouseEvent) => {
        e.preventDefault();
        setCanSplit(isCellSplitEligible(cellElement));
        // Right-click INSIDE an existing multi-cell selection: preserve the
        // selection so the menu's operations target every selected cell.
        // Right-click OUTSIDE (single cell, or different cell): clear any stale
        // library multi-cell selection, then select THIS cell so the menu's
        // transforms (and the toolbar background / align, which read the
        // selection) target the right row + column.
        if (!isInSelection) {
            try {
                const tablePlugin = (editor as any)?.getPlugin?.({ key: 'table' });
                tablePlugin?.tf?.setOption?.('_selectedCellIds', undefined);
            } catch {
                /* defensive: editor may not be ready */
            }
            selectCell(editor, cellElement);
        } else {
            // A multi-cell selection owns the highlight — drop any single-cell.
            clearCellSelection(editor);
        }
        setMenuPos({ x: e.clientX, y: e.clientY });
    };

    const close = useCallback(() => setMenuPos(null), []);

    // Programmatic row/column selection. Strategy: set a Slate range from
    // the start of the first cell in the row/column to the end of the last
    // cell. Plate's table plugin reads this range, computes its bounding
    // rectangle via `getTableGridByRange`, and highlights every cell inside
    // — which for a single-row range is the row, and for a single-column
    // range (top-left → bottom-left of the column) is the column.
    //
    // We resolve the cell's grid coordinates by walking up from the cell
    // path (slate path = [tableIdx, rowIdx, colIdx]). Defensive guards
    // protect against weird paths (e.g. cell removed before menu fires).
    const selectRowOrColumn = (mode: 'row' | 'column') => {
        try {
            const ed: any = editor;
            const cellPath: number[] | undefined = ed?.api?.findPath?.(cellElement);
            if (!cellPath || cellPath.length < 2) return;
            const colIdx = cellPath[cellPath.length - 1];
            const rowIdx = cellPath[cellPath.length - 2];
            const tablePath = cellPath.slice(0, -2);
            const tableNode: any = ed?.api?.node?.(tablePath)?.[0];
            const { start: startPath, end: endPath } = calculateSelectionRange(
                mode,
                tableNode,
                tablePath,
                mode === 'row' ? rowIdx : colIdx,
            );
            if (!startPath || !endPath) return;
            const start = ed?.api?.start?.(startPath);
            const end = ed?.api?.end?.(endPath);
            if (start && end) {
                ed?.tf?.select?.({ anchor: start, focus: end });
                ed?.tf?.focus?.();
            }
        } catch {
            /* defensive — table may have been mutated under us */
        }
    };

    const setVerticalAlign = (value: 'top' | 'middle' | 'bottom') => {
        try {
            const ed: any = editor;
            const tableApi = ed?.api?.table ?? ed?.getApi?.({ key: 'table' })?.table;
            const selected = tableApi?.getSelectedCells?.();
            if (Array.isArray(selected) && selected.length > 1) {
                // Multi-cell — set on every selected cell node.
                ed?.tf?.setNodes?.({ verticalAlign: value }, { match: (n: any) => selected.includes(n) });
                return;
            }
            const path = ed?.api?.findPath?.(cellElement);
            if (path) ed?.tf?.setNodes?.({ verticalAlign: value }, { at: path });
        } catch {
            /* defensive */
        }
    };

    // Collapse the caret into the right-clicked cell before a row/column delete.
    // `selectCell` (single-cell right-click) leaves an EXPANDED full-cell range;
    // platejs's deleteRow/deleteColumn route an expanded selection into their
    // multi-cell "grid" path (deleteRowWhenExpanded), which only removes when the
    // selection spans the ENTIRE row/column (acrossColumn === columnCount) — so a
    // single-cell selection deletes NOTHING (the reported bug). A collapsed caret
    // takes the single-row/column path that resolves the target from the cell.
    // Skipped when a real multi-cell selection is active (isInSelection) so a
    // range delete still removes every selected row/column.
    const collapseForDelete = () => {
        if (isInSelection) return;
        try {
            const path = (editor as any)?.api?.findPath?.(cellElement);
            const start = path && (editor as any)?.api?.start?.(path);
            if (start) (editor as any)?.tf?.select?.(start);
        } catch {
            /* defensive — cell may have been mutated under us */
        }
    };

    // `verticalAlign` is a LOCAL extension on td/th nodes — Plate's renderer
    // doesn't know about it; TableCellElement reads it and applies an inline
    // style. If HTML export is added later, register a custom td/th
    // serializer or this property silently drops.
    // Built only while the menu is OPEN (menuPos set). Closed, this is a shared
    // empty array, so every Cell render (typing, selection, scroll) skips
    // allocating ~15 entries and their closures. `canSplit` is resolved in open()
    // before menuPos is set, so it is already current whenever this list is built.
    const entries: CellContextMenuEntry[] = menuPos
        ? [
              { label: 'Select row', onClick: () => selectRowOrColumn('row') },
              { label: 'Select column', onClick: () => selectRowOrColumn('column') },
              { divider: true },
              {
                  label: 'Insert row above',
                  onClick: () => (editor as any).tf?.insert?.tableRow?.({ before: true }),
              },
              {
                  label: 'Insert row below',
                  onClick: () => (editor as any).tf?.insert?.tableRow?.(),
              },
              { divider: true },
              {
                  label: 'Insert column left',
                  onClick: () => insertTableColumnSized(editor, cellElement, true),
              },
              {
                  label: 'Insert column right',
                  onClick: () => insertTableColumnSized(editor, cellElement),
              },
              { divider: true },
              // Split is kept so users can manually break apart any merged cell that
              // sneaks in via paste; Merge is intentionally absent — the migration
              // unmerges every cell on load.
              ...(canSplit
                  ? ([{ label: 'Split cell', onClick: () => (splitTableCell as any)(editor) }, { divider: true }] as CellContextMenuEntry[])
                  : []),
              { label: 'Align top', onClick: () => setVerticalAlign('top') },
              { label: 'Align middle', onClick: () => setVerticalAlign('middle') },
              { label: 'Align bottom', onClick: () => setVerticalAlign('bottom') },
              { divider: true },
              {
                  label: 'Delete row',
                  danger: true,
                  onClick: () => {
                      collapseForDelete();
                      deleteTableRowWithSpanFix(editor);
                  },
              },
              {
                  label: 'Delete column',
                  danger: true,
                  onClick: () => {
                      collapseForDelete();
                      deleteTableColumnWithSpanFix(editor);
                  },
              },
              {
                  label: 'Delete table',
                  danger: true,
                  onClick: () => (editor as any).tf?.remove?.table?.(),
              },
          ]
        : EMPTY_CELL_MENU_ENTRIES;

    return { menuPos, open, close, entries };
}

// @platejs/resizable's <ResizeHandle> is a low-level primitive: it does NOT
// preventDefault on mousedown, does NOT lock document.body.style.cursor, and
// does NOT set body.userSelect = 'none' during the drag. The old hand-rolled
// ColumnResizeHandle / RowResizeHandle did all three, which is what kept the
// drag clean. Without those locks:
//   - the browser starts native text selection from the click point and
//     extends it across the row/below rows as the pointer moves
//   - the cursor flickers between col-resize/row-resize and text whenever
//     the pointer leaves the 4-px handle strip
// Capture-phase mousedown fires before the library's bubble-phase handler,
// so we can restore the locks here without fighting Plate's resize state.
function makeResizeLockHandler(cursor: 'col-resize' | 'row-resize') {
    return (e: React.MouseEvent) => {
        e.preventDefault();
        const body = document.body;
        const prevCursor = body.style.cursor;
        const prevSelect = body.style.userSelect;
        body.style.cursor = cursor;
        body.style.userSelect = 'none';
        const release = () => {
            body.style.cursor = prevCursor;
            body.style.userSelect = prevSelect;
            window.removeEventListener('mouseup', release);
            window.removeEventListener('touchend', release);
            window.removeEventListener('touchcancel', release);
        };
        window.addEventListener('mouseup', release);
        window.addEventListener('touchend', release);
        window.addEventListener('touchcancel', release);
    };
}
const lockColResize = makeResizeLockHandler('col-resize');
const lockRowResize = makeResizeLockHandler('row-resize');

const Cell = React.memo(function Cell({ as, ...props }: any) {
    countRender('table-cell');
    const readOnly = useReadOnly();
    const editor = useEditorRef();
    // Single library hook — gives us colIndex / colSpan / rowIndex / selected /
    // borders / width / minHeight / isSelectingCell in one call. Replaces our
    // earlier hand-wired useCellIndices + useIsCellSelected pair.
    const cellState: any = (useTableCellElement as any)();
    // librarySelected = @platejs/table multi-cell selection (>1 gate);
    // singleSelected = our Excel-style single-cell selection (left-click /
    // right-click / arrow nav), held editor-wide in a TablePlugin option and
    // read reactively here. Either lights up `.cellSelected`. Compared by NODE
    // identity (stable across renders) so no findPath cost per cell.
    const librarySelected: boolean = Boolean(cellState?.selected);
    const { show: showHover } = useTableHover();
    // Subscribe with a per-cell SELECTOR, not the raw atom value: usePluginOptions
    // re-renders this cell only when ITS OWN selected-boolean flips, so selecting a
    // cell re-renders just the two affected cells (old + new) instead of every cell
    // in the document. usePluginOption returned the whole atom, so every mounted
    // cell re-rendered on any selection change — the table-cell-select lag. React
    // identity of props.element is stable across selection changes (same node),
    // matching the previous `=== props.element` comparison.
    const singleSelected: boolean = Boolean(
        (usePluginOptions as any)(TablePlugin, (state: any) => {
            const sel = state?.[CELL_SELECTION_OPTION];
            return sel != null && sel === props.element;
        }),
    );
    const selected: boolean = librarySelected || singleSelected;
    // Is THIS cell the one being edited (collapsed caret inside it, and not in
    // "selected" mode)? Drives the text cursor; otherwise cells show the
    // spreadsheet cell-select cursor. Short-circuits before the (cheap, shallow)
    // api.above walk so hover / selected mode pay no traversal cost.
    const isEditing: boolean = (useEditorSelector as any)(
        (ed: any) => {
            if (getSelectedCell(ed)) return false;
            if (!ed?.selection || !ed?.api?.isCollapsed?.()) return false;
            try {
                const above = ed?.api?.above?.({ match: (n: any) => CELL_TYPES.includes(n?.type) });
                return above?.[0] === props.element;
            } catch {
                return false;
            }
        },
        [props.element],
    );
    // INTENTIONAL: we do NOT apply cellState.minHeight as inline style on
    // the cell. The library plumbs the row-size override into every cell's
    // minHeight, which caused N reflows per drag tick (laggy row resize).
    // TableRowElement above now reads the override directly from the table
    // store and applies `height` at the <tr> level — one reflow per tick.
    // Cells inside stretch to fill the row via standard table layout, so
    // no visual difference, just much smoother dragging.
    // Library hook returns props for three resize handles (right / bottom /
    // left). Each handle's drag fires a store-level override (no DOM mutation
    // by us), commits via setTableColSize / setTableRowSize on release. Drag
    // previews now survive any Cell re-render because the override flows
    // through useTableColSizes → <col> widths reactively.
    // NOTE: `leftProps`/`hiddenLeft` (the leftmost-column resize handle) are
    // intentionally NOT destructured/rendered. Clicking the first column's left
    // edge committed a width change that shrank the whole table, so that handle
    // is disabled for now — the first column is still resizable via its right edge.
    const { rightProps, bottomProps } = (useTableCellElementResizable as any)({
        colIndex: cellState?.colIndex ?? 0,
        colSpan: cellState?.colSpan ?? 1,
        rowIndex: cellState?.rowIndex ?? 0,
    });
    const { menuPos, open, close, entries } = useCellMenu(props.element, librarySelected);
    // Plate's @platejs/table `setCellBackground` writes the property as
    // `background` (not `backgroundColor`). Our single-cell paint path in
    // CellBackgroundMenu used to write `backgroundColor`, so we read EITHER
    // here. New cell fills should land under `background` going forward —
    // see the serializers (slateToPlate / plateToSlate) for the round-trip
    // mapping that converts to/from the legacy Slate `backgroundColor` key.
    const cellBg: string | undefined = props.element?.background ?? props.element?.backgroundColor;
    const verticalAlign: string | undefined = props.element?.verticalAlign;
    // Forward the LOGICAL span (top-level colSpan/rowSpan, legacy attributes.*
    // fallback — the same shape @platejs/table's getColSpan/getRowSpan read) onto
    // the physical <td>/<th> so the rendered DOM grid matches the model. Without
    // it a merged cell rendered as a plain 1x1 cell and the DOM grid drifted from
    // the index matrix. Emitted only when > 1 (a normal cell needs no attribute).
    const rawColSpan = props.element?.colSpan ?? props.element?.attributes?.colspan;
    const rawRowSpan = props.element?.rowSpan ?? props.element?.attributes?.rowspan;
    const domColSpan: number | undefined = Number(rawColSpan) > 1 ? Number(rawColSpan) : undefined;
    const domRowSpan: number | undefined = Number(rawRowSpan) > 1 ? Number(rawRowSpan) : undefined;

    // Are we already EDITING inside this cell? (caret/selection in this cell AND
    // not in the "selected" mode). mousedown fires BEFORE the browser moves the
    // caret, so editor.selection still reflects the pre-click position — i.e.
    // "was I editing this cell before this click?". If so, a single click should
    // reposition the caret (default), NOT kick back to selected mode.
    const isEditingThisCell = (): boolean => {
        if (getSelectedCell(editor)) return false;
        const sel: any = (editor as any)?.selection;
        const anchorPath: number[] | undefined = sel?.anchor?.path;
        if (!anchorPath) return false;
        try {
            const cellPath: number[] | undefined = (editor as any)?.api?.findPath?.(props.element);
            if (!cellPath) return false;
            return anchorPath.slice(0, cellPath.length).join() === cellPath.join();
        } catch {
            return false;
        }
    };

    // Excel-style click model:
    //   • right mousedown  → suppress caret jump; open() (contextmenu) selects.
    //   • shift + click    → extend the selection from the current anchor cell
    //                        to this cell (rectangle between the two). Checked
    //                        FIRST — before the double-click / re-click-to-edit
    //                        branches below — so shift+click always extends,
    //                        even when clicking the already-selected cell.
    //   • double-click     → enter EDIT mode: drop the selection, let the browser
    //                        place the caret where clicked.
    //   • single click     → SELECT the cell (unless already editing it): suppress
    //                        the native caret and range-select the whole cell;
    //                        CSS (.cellSelected) hides the caret + ::selection so
    //                        it reads as a clean cell highlight.
    //   • drag             → let @platejs/table's native multi-cell range stand:
    //                        the cross-cell editor.selection produced by the
    //                        browser's contentEditable drag is read straight off
    //                        editor.selection by getTableGridAbove (>1 gate), so
    //                        we just stay out of the way — no preventDefault, no
    //                        selectCell.
    //
    // mousedown fires BEFORE any pointer movement, so we cannot yet tell a click
    // from a drag. watchCellDrag defers the decision to mouseup: we arm the
    // native drag (no preventDefault) and on mouseup either run the single-cell
    // selectCell (click) or do nothing (drag — native range already lit it).
    const handleMouseDown = (e: React.MouseEvent) => {
        if (e.button === 2) {
            e.preventDefault();
            return;
        }
        if (e.button !== 0) return;
        if (e.shiftKey) {
            e.preventDefault(); // suppress native shift-click text/caret extension
            if (selectCellRange(editor, props.element)) return;
            // No usable anchor (e.g. nothing selected yet in this table) —
            // fall through to a normal single-cell select below.
        }
        if (e.detail >= 2) {
            clearCellSelection(editor);
            return; // no preventDefault → default caret placement = editing
        }
        // Second (separate) single click on the already-selected cell → enter edit
        // mode: drop the cell selection and let the browser place the caret at the
        // click point, so a double-click isn't needed to start typing. Mirrors the
        // double-click path above; `singleSelected` is THIS cell's own selected
        // state. A drag still range-selects natively (no preventDefault here).
        if (singleSelected) {
            clearCellSelection(editor);
            return;
        }

        if (isEditingThisCell()) return; // mid-edit: let the click move the caret

        // Drop any stale single-cell highlight up front: if this gesture turns
        // into a drag, the native multi-cell wash must own the highlight alone
        // (a lingering singleSelectedCell would paint a second .cellSelected).
        // A confirmed click re-selects this cell below, restoring single-select.
        clearCellSelection(editor);

        const cellNode = props.element;
        watchCellDrag(e.clientX, e.clientY, (dragged) => {
            if (dragged) return; // native @platejs/table range already lit the rectangle
            selectCell(editor, cellNode);
        });
    };

    // Surface this cell's (rowIndex, colIndex) to the table-level hover
    // context so the floating row/column drag handles know where to anchor.
    // No-op on mouseleave — hide is handled at the wrapper level (with a
    // short delay) so the pointer can travel from cell border to handle
    // without flicker.
    const handleMouseEnter = () => {
        showHover(cellState?.rowIndex ?? 0, cellState?.colIndex ?? 0);
    };

    // Read-only short-circuit: history-preview / archived views shouldn't be
    // drag-resizable or right-clickable into destructive transforms. Skip the
    // resize handles AND the context-menu wiring entirely.
    const spanAttributes =
        domColSpan || domRowSpan
            ? { ...(domColSpan ? { colSpan: domColSpan } : {}), ...(domRowSpan ? { rowSpan: domRowSpan } : {}) }
            : undefined;
    const cellAttributes = readOnly
        ? spanAttributes
            ? { ...props.attributes, ...spanAttributes }
            : props.attributes
        : { ...props.attributes, ...spanAttributes, onContextMenu: open, onMouseDown: handleMouseDown, onMouseEnter: handleMouseEnter };

    return (
        <>
            <PlateElement
                as={as}
                {...props}
                attributes={cellAttributes}
                className={clsx(props.className, styles.cell, selected && styles.cellSelected, isEditing && styles.cellEditing)}
                style={{
                    ...(props.style ?? {}),
                    // ResizeHandles below are `position: absolute`. Without an
                    // unconditional positioning context on the cell, they
                    // resolve to the nearest positioned ancestor (which varies
                    // between selected/unselected — `.cellSelected` is the only
                    // other source of `position: relative`). Pin it here so the
                    // handles always anchor to the cell box.
                    position: 'relative',
                    border: '1px solid #d8d6d2',
                    padding: '10px 12px',
                    ...(cellBg ? { backgroundColor: cellBg } : {}),
                    ...(verticalAlign ? { verticalAlign } : {}),
                }}
            >
                {props.children}
                {!readOnly && (
                    <>
                        {/*
                         * `{...rightProps/bottomProps/leftProps}` from
                         * useTableCellElementResizable spreads the library's
                         * inline `style` (position, cursor, drag handlers AND
                         * an oversized grab zone — ~8–10 px). Inline style
                         * wins over our SCSS, so the visible handle (`.colHandle`
                         * etc.) is silently inflated and the resize cursor
                         * fires well inside the cell. Re-apply our intended
                         * 4 px touch strip + `-2 px` offset AFTER the spread
                         * to clip the zone back to the cell edge. Library
                         * handlers stay intact because we only override
                         * dimensions, not the event props.
                         */}
                        <ResizeHandle
                            {...rightProps}
                            className={styles.colHandle}
                            contentEditable={false}
                            // Hit strip is 10 px wide, biased INWARD (right: -0.5
                            // keeps the strip's right edge — and therefore the
                            // ::after visible line — pinned to the cell border).
                            // Previously width:1 made the cursor flicker as the
                            // pointer left a 1 px zone with the slightest move.
                            style={{ ...(rightProps?.style ?? {}), width: 10, right: -0.5 }}
                            onMouseDownCapture={lockColResize}
                        />
                        <ResizeHandle
                            {...bottomProps}
                            className={styles.rowHandle}
                            contentEditable={false}
                            // Same widening for row resize — height: 10 biased
                            // upward (bottom: -0.5 keeps the visible line on
                            // the cell's bottom border).
                            style={{ ...(bottomProps?.style ?? {}), height: 10, bottom: -0.5 }}
                            onMouseDownCapture={lockRowResize}
                        />
                    </>
                )}
            </PlateElement>
            {!readOnly && menuPos && <CellContextMenu x={menuPos.x} y={menuPos.y} entries={entries} onClose={close} />}
        </>
    );
});

export const TableCellElement = React.memo(function TableCellElement(props: any) {
    return <Cell as="td" {...props} />;
});

export const TableCellHeaderElement = React.memo(function TableCellHeaderElement(props: any) {
    return <Cell as="th" {...props} />;
});
